import {
  FormControl,
  InputLabel,
  MenuItem,
  FormHelperText,
  Select as MuiSelect,
  CircularProgress,
} from "@mui/material";
import {
  Controller,
  FieldPath,
  FieldValues,
  get,
  useFormContext,
} from "react-hook-form";
import { getErrorMessage, SelectOption } from "../fieldTypes";

interface SelectProps<FormValues extends FieldValues = FieldValues> {
  label: string;
  name: FieldPath<FormValues>;
  options?: SelectOption[];
  multiple?: boolean;
  disabled?: boolean;
  displayEmpty?: boolean;
  isLoading?: boolean;
}

const Select = <FormValues extends FieldValues = FieldValues>({
  label,
  name,
  options = [],
  multiple = false,
  disabled = false,
  displayEmpty = true,
  isLoading = false,
}: SelectProps<FormValues>) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<FormValues>();
  const error = get(errors, name) as unknown;
  const errorMsg = getErrorMessage(error);

  const getShrinkValue = (value: unknown): boolean => {
    if (multiple) {
      return true;
    }

    if (!options?.length && displayEmpty) {
      return true;
    }

    if (displayEmpty) {
      return true;
    }

    if (value === 0) {
      return true;
    }

    return !!value;
  };

  return (
    <FormControl fullWidth error={!!error}>
      <Controller
        name={name}
        control={control}
        render={({ field: { onChange, value } }) => (
          <>
            <InputLabel
              size="small"
              id="demo-simple-select-label"
              shrink={getShrinkValue(value)}
            >
              {label}
            </InputLabel>
            <MuiSelect
              labelId="demo-simple-select-label"
              label={label}
              value={value}
              onChange={onChange}
              multiple={multiple}
              disabled={disabled}
              error={!!error}
              size="small"
              fullWidth
              displayEmpty={displayEmpty}
              renderValue={(selected: unknown) => {
                if (isLoading) {
                  return <CircularProgress size={18} />;
                }
                if (Array.isArray(selected) && selected.length === 0) {
                  return "Select options";
                }
                if (multiple) {
                  const selectedValues = Array.isArray(selected)
                    ? selected
                    : [];
                  const selectedLabels = options
                    .filter((option) =>
                      selectedValues.some(
                        (selectedValue) => option.value === selectedValue
                      )
                    )
                    .map((el) => el.caption || el.label);
                  return selectedLabels;
                }
                const found = options?.find(
                  (option) => option.value === selected
                );

                return found?.caption || found?.label || String(selected ?? "");
              }}
            >
              {isLoading && (
                <MenuItem key={value} value={value}>
                  <CircularProgress size={18} />
                </MenuItem>
              )}
              {!isLoading &&
                (options || []).map(
                  ({ value, label, caption, disabled: optionDisabled }) => (
                    <MenuItem
                      key={value}
                      value={value}
                      disabled={optionDisabled}
                    >
                      {caption || label}
                    </MenuItem>
                  )
                )}
            </MuiSelect>
          </>
        )}
      />
      <FormHelperText>{errorMsg}</FormHelperText>
    </FormControl>
  );
};

export { Select };
